
import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { X, ChevronLeft, ChevronRight, Check } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';

// Import step components
import Step1MergeType from './steps/Step1MergeType';
import Step2DestinationBus from './steps/Step2DestinationBus';
import Step3SourceBuses from './steps/Step3SourceBuses';
import Step4ScopeDate from './steps/Step4ScopeDate';
import Step5Students from './steps/Step5Students';
import Step6RouteOrder from './steps/Step6RouteOrder';
import Step7Driver from './steps/Step7Driver';
import Step8Notification from './steps/Step8Notification';
import Step9Summary from './steps/Step9Summary';

const TOTAL_STEPS = 9;

const STEP_TITLES = [
  'Merge Type',
  'Destination Bus',
  'Source Buses',
  'Scope & Date',
  'Students',
  'Route Order',
  'Driver',
  'Notification',
  'Activate & Summary',
];

const BusMergeWizard = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [showExitDialog, setShowExitDialog] = useState(false);

  // Wizard state
  const [wizardData, setWizardData] = useState({
    mergeType: null, // 'instant' or 'scheduled'
    destinationBus: null,
    sourceBuses: [],
    shift: null,
    mergeDate: null,
    selectedStudents: [],
    routeOrder: [],
    driver: null,
    useDefaultDriver: true,
    notification: {
      message: '',
      recipients: [],
      timing: 'immediate',
    },
  });

  const updateWizardData = (field, value) => {
    setWizardData((prev) => ({ ...prev, [field]: value }));
  };

  const handleNext = () => {
    if (currentStep < TOTAL_STEPS) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handlePrevious = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleClose = () => {
    setShowExitDialog(true);
  };

  const confirmExit = () => {
    navigate('/dashboard/live-tracking');
  };

  const canProceed = () => {
    switch (currentStep) {
      case 1:
        return wizardData.mergeType !== null;
      case 2:
        return wizardData.destinationBus !== null;
      case 3:
        return wizardData.sourceBuses.length > 0;
      case 4:
        return wizardData.mergeType === 'instant' || (wizardData.shift && wizardData.mergeDate);
      case 5:
        return wizardData.selectedStudents.length > 0;
      case 6:
        return wizardData.routeOrder.length > 0;
      case 7:
        return wizardData.useDefaultDriver || wizardData.driver !== null;
      case 8:
        return true;
      case 9:
        return true;
      default:
        return false;
    }
  };

  const renderStep = () => {
    const stepProps = {
      wizardData,
      updateWizardData,
      onNext: handleNext,
    };

    switch (currentStep) {
      case 1:
        return <Step1MergeType {...stepProps} />;
      case 2:
        return <Step2DestinationBus {...stepProps} />;
      case 3:
        return <Step3SourceBuses {...stepProps} />;
      case 4:
        return <Step4ScopeDate {...stepProps} />;
      case 5:
        return <Step5Students {...stepProps} />;
      case 6:
        return <Step6RouteOrder {...stepProps} />;
      case 7:
        return <Step7Driver {...stepProps} />;
      case 8:
        return <Step8Notification {...stepProps} />;
      case 9:
        return <Step9Summary {...stepProps} onComplete={confirmExit} />;
      default:
        return null;
    }
  };

  return (
    <div className="min-h-screen bg-[#f8f9fb]">
      <div className="mx-auto p-6">
        {/* Header */}
        <div className="flex items-center justify-between mb-6">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Bus Merge Session</h1>
            <p className="text-sm text-gray-500 mt-1">
              Combine multiple buses into a single operational bus
            </p>
          </div>
          <Button
            variant="ghost"
            size="icon"
            onClick={handleClose}
            className="text-gray-400 hover:text-gray-600"
          >
            <X className="h-5 w-5" />
          </Button>
        </div>

        {/* Progress Indicator */}
        <Card className="mb-6 p-6">
          <div className="flex items-center justify-between mb-4">
            {STEP_TITLES.map((title, index) => {
              const stepNumber = index + 1;
              const isActive = stepNumber === currentStep;
              const isCompleted = stepNumber < currentStep;

              return (
                <React.Fragment key={stepNumber}>
                  <div className="flex flex-col items-center">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all ${
                        isCompleted
                          ? 'bg-blue-600 text-white'
                          : isActive
                          ? 'bg-blue-600 text-white ring-4 ring-blue-100'
                          : 'bg-gray-200 text-gray-500'
                      }`}
                    >
                      {isCompleted ? <Check className="h-5 w-5" /> : stepNumber}
                    </div>
                    <span
                      className={`text-xs mt-2 text-center max-w-[80px] ${
                        isActive ? 'text-blue-600 font-semibold' : 'text-gray-500'
                      }`}
                    >
                      {title}
                    </span>
                  </div>
                  {index < STEP_TITLES.length - 1 && (
                    <div
                      className={`flex-1 h-0.5 mx-2 transition-all ${
                        stepNumber < currentStep ? 'bg-blue-600' : 'bg-gray-200'
                      }`}
                    />
                  )}
                </React.Fragment>
              );
            })}
          </div>
          <div className="text-center text-sm text-gray-500">
            Step {currentStep} / {TOTAL_STEPS}
          </div>
        </Card>

        {/* Step Content */}
        <Card className="mb-6 p-6">
          <h2 className="text-xl font-semibold text-gray-900 mb-4">
            {STEP_TITLES[currentStep - 1]}
          </h2>
          {renderStep()}
        </Card>

        {/* Navigation Buttons */}
        <div className="flex items-center justify-between">
          <Button
            variant="outline"
            onClick={handlePrevious}
            disabled={currentStep === 1}
            className="gap-2"
          >
            <ChevronLeft className="h-4 w-4" />
            Previous
          </Button>
          <Button
            onClick={handleNext}
            disabled={!canProceed() || currentStep === TOTAL_STEPS}
            className="bg-blue-600 hover:bg-blue-700 text-white gap-2"
          >
            Next
            <ChevronRight className="h-4 w-4" />
          </Button>
        </div>
      </div>

      {/* Exit Confirmation Dialog */}
      <Dialog open={showExitDialog} onOpenChange={setShowExitDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Exit Merge Session?</DialogTitle>
            <DialogDescription>
              Are you sure you want to exit? All progress will be lost.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowExitDialog(false)}>
              Cancel
            </Button>
            <Button
              variant="destructive"
              onClick={confirmExit}
            >
              Exit
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default BusMergeWizard;
